import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useSearchParams } from 'react-router-dom';
import config from './config';

interface AcceptInviteResponse {
  message?: string;
  organizationId?: number;
  organizationSlug?: string;
  membershipId?: number;
}

const OrganizationInviteAccept: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [acceptedOrganizationSlug, setAcceptedOrganizationSlug] = useState('');
  const hasAutoTriedRef = useRef(false);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const authToken =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!authToken) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return authToken;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const authToken = await getAccessToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${authToken}`
        }
      });
    },
    [getAccessToken]
  );

  const handleAccept = useCallback(async () => {
    if (!token) {
      setError('Invalid invite link. Token is missing.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setNotice('');

      const response = await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/invites/accept`,
        data: { token }
      });

      const data = (response.data ?? {}) as AcceptInviteResponse;
      setAcceptedOrganizationSlug(data.organizationSlug?.trim() || '');

      setNotice('Invite accepted. You are now a member of the organization.');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to accept invite.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, withAuth]);

  useEffect(() => {
    if (!isAuthenticated || !token || hasAutoTriedRef.current) {
      return;
    }

    hasAutoTriedRef.current = true;
    handleAccept();
  }, [handleAccept, isAuthenticated, token]);

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 760, margin: '32px auto', padding: '0 16px 24px' }}>
        <h2>Accept Organization Invite</h2>
        <p>You need to sign in first to accept this invite.</p>
        <button
          onClick={() => {
            const returnTo = `${window.location.pathname}${window.location.search}`;
            sessionStorage.setItem('postLoginReturnTo', returnTo);
            loginWithRedirect({
              appState: {
                returnTo,
              },
            });
          }}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '32px auto', padding: '0 16px 24px' }}>
      <h2 style={{ marginBottom: 8 }}>Accept Organization Invite</h2>
      <p style={{ color: '#4b5563', marginBottom: 20 }}>Accept your invitation and join the organization.</p>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ background: '#dcfce7', color: '#166534', padding: 12, borderRadius: 6, marginBottom: 12 }}>{notice}</div>}

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
        {!notice && (
          <>
            <p style={{ marginTop: 0, color: '#6b7280', fontSize: 14 }}>Invite token: {token ? 'Detected' : 'Missing'}</p>
            <button
              onClick={handleAccept}
              disabled={loading}
              style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 14px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontWeight: 600 }}
            >
              {loading ? 'Accepting...' : 'Accept Invite'}
            </button>
          </>
        )}

        {notice && acceptedOrganizationSlug && (
          <Link
            to={`/${acceptedOrganizationSlug}/analytics`}
            style={{ display: 'inline-block', background: '#111827', color: '#fff', borderRadius: 6, padding: '10px 14px', textDecoration: 'none', fontWeight: 600 }}
          >
            Continue to Analytics
          </Link>
        )}

        {notice && !acceptedOrganizationSlug && (
          <Link to="/analytics?pre=1" style={{ color: '#2563eb', textDecoration: 'none' }}>Continue to Analytics</Link>
        )}
      </div>
    </div>
  );
};

export default OrganizationInviteAccept;
